import { isPlatformBrowser } from '@angular/common';
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';

import { BehaviorSubject, Observable } from 'rxjs';

import { environment } from 'environments/environment';

import { Development, Enquirer, Properties } from '@core/types';
import { BrazeChildList } from '@core/types/braze/child-list.model';
import { PageDetails } from '@core/types/braze/page-details.model';
import { BrazeProject } from '@core/types/braze/project.model';

interface BrazeProjectChildListModel extends BrazeProject, BrazeChildList {}

@Injectable({
  providedIn: 'root',
})
export class BrazeService {
  isBrowser = false;
  window: Window;
  braze;
  _brazeLoading = new BehaviorSubject<boolean>(false);

  constructor(
    @Inject(PLATFORM_ID) platformId: string,
    @Inject(DOCUMENT) private document: Document
  ) {
    this.window = this.document.defaultView;
    this.isBrowser = isPlatformBrowser(platformId);
    this.initialize();
  }

  public set setBrazeLoading(resetStatus: boolean) {
    this._brazeLoading.next(resetStatus);
  }

  public get brazeLoading(): Observable<boolean> {
    return this._brazeLoading.asObservable();
  }

  initialize(): void {
    if (this.isBrowser) {
      import('@braze/web-sdk').then(braze => {
        this.braze = braze;
        this.braze.initialize(environment.brazeApiKey, {
          baseUrl: environment.brazeEndpoint,
          allowUserSuppliedJavascript: true,
        });
        this.braze.openSession();
        this.setBrazeLoading = true;
      });
    }
  }

  userEnquired(
    enquirer: Enquirer,
    development: Development,
    property?: Properties
  ): void {
    const enquiryData = this.getEnquirerDetails(enquirer);
    const brazeProject = this.getBrazeProject(development);
    const brazeChildList = this.getBrazeChildList(property);

    property
      ? this.propertyEnquiry(enquiryData, brazeProject, brazeChildList)
      : this.projectEnquiry(enquiryData, brazeProject);
  }

  projectEnquiry(enquirer, brazeProject: BrazeProject) {
    const data = {
      page_type: 'project_listing',
      ...enquirer,
      ...brazeProject,
    };

    this.brazeLoading.subscribe((res: boolean) => {
      if (res) {
        this.braze.logCustomEvent('form_enquired', data);
      }
    });
  }

  propertyEnquiry(
    enquirer,
    brazeProject: BrazeProject,
    brazeProjectChildList?: BrazeProjectChildListModel
  ) {
    const data = {
      page_type: 'child_listing',
      ...enquirer,
      ...brazeProject,
      ...brazeProjectChildList,
    };

    this.brazeLoading.subscribe((res: boolean) => {
      if (res) {
        this.braze.logCustomEvent('form_enquired', data);
      }
    });
  }

  projectViewed(development?: Development): void {
    const brazeProject = this.getBrazeProject(development);
    const GAData = this.getGAData();

    const projectChildListModel: BrazeProjectChildListModel = {
      ...brazeProject,
      ...GAData,
      page_type: 'project_listing',
    };

    this.brazeLoading.subscribe((res: boolean) => {
      if (res) {
        this.braze.logCustomEvent('page_view', projectChildListModel);
      }
    });
  }

  childListViewed(development?: Development, property?: Properties): void {
    const brazeProject = this.getBrazeProject(development);
    const brazeChildList = this.getBrazeChildList(property);
    const GAData = this.getGAData();

    const projectChildListModel: BrazeProjectChildListModel = {
      ...brazeProject,
      ...brazeChildList,
      ...GAData,
      page_type: 'child_listing',
    };

    this.brazeLoading.subscribe((res: boolean) => {
      if (res) {
        this.braze.logCustomEvent('page_view', projectChildListModel);
      }
    });
  }

  searchViewed(type: string): void {
    const pageDetails = this.getPageDetails();

    const searchPage: PageDetails = {
      ...pageDetails,
      page_type: type,
    };

    this.brazeLoading.subscribe((res: boolean) => {
      if (res) {
        this.braze.logCustomEvent('page_view', searchPage);
      }
    });
  }

  homeViewed(): void {
    const pageDetails: PageDetails = {
      page_url: this.window?.location?.href,
      page_type: 'home',
    };

    this.brazeLoading.subscribe((res: boolean) => {
      if (res) {
        this.braze.logCustomEvent('page_view', pageDetails);
      }
    });
  }

  newsViewed(category: string): void {
    const propertyNews = {
      page_type: 'news',
      page_url: this.window?.location?.href,
      news_category: category,
    };

    this.brazeLoading.subscribe((res: boolean) => {
      if (res) {
        this.braze.logCustomEvent('page_view', propertyNews);
      }
    });
  }

  industryProfiles() {
    const data: BrazeProject = {
      project_name: '',
      project_state: '',
      project_region: '',
      project_suburb: '',
      project_postcode: '',
    };

    return data;
  }

  getGAData() {
    const cookies = this.getCookies();

    const result = Object.keys(cookies)
      .filter(function (k) {
        return k.indexOf('_g') == 0;
      })
      .reduce(function (newData, k) {
        newData[k] = cookies[k];
        return newData;
      }, {});

    return result;
  }

  getEnquirerDetails(enquirer: Enquirer) {
    const result = {
      user_postcode: enquirer?.postCode,
      user_buyer_type: enquirer?.audience,
      user_price_range: enquirer?.priceRange,
      user_timeframe: enquirer?.lookingToBuy,
      user_contact_method: enquirer?.contactMethod,
      user_finance_approval: enquirer?.selectPreApproval,
    };
    return result;
  }

  getDevelopmentDetails(development: BrazeProject) {
    const project = {
      project_enquired: development?.project_name,
      state_enquired: development?.project_state,
      region_enquired: development?.project_region,
      suburb_enquired: development?.project_suburb,
      postcode_enquired: development?.project_postcode,
      price_min_enquired: development?.project_from_price,
      price_max_enquired: development?.project_to_price,
      bedroom_min_enquired: development?.project_min_bed,
      bedroom_max_enquired: development?.project_max_bed,
      bath_min_enquired: development?.project_min_bath,
      bath_max_enquired: development?.project_max_bath,
      study_min_enquired: development?.project_min_study,
      study_max_enquired: development?.project_max_study,
      carpark_min_enquired: development?.project_min_carpark,
      carpark_max_enquired: development?.project_max_carpark,
      earliest_expected_completion_enquired:
        development?.project_expected_completion,
    };

    return project;
  }

  getCookies(): { [key: string]: string } {
    const cookies: { [key: string]: string } = {};

    if (this.document.cookie) {
      this.document.cookie.split(';').forEach(cookie => {
        const [key, value] = cookie.trim().split('=');
        cookies[key] = value;
      });
    }
    return cookies;
  }

  changeUser(id: string): void {
    this.braze.changeUser(id);
  }

  getBrazeProject(development: Development): BrazeProject {
    const bedrooms = this.getRange(development?.bedrooms);
    const bathrooms = this.getRange(development?.bathrooms);
    const carSpaces = this.getRange(development?.carSpaces);
    const study = this.getRange(development?.studyroom);
    const priceFrom = this.transformNumber(development?.priceSearch);
    const priceTo = this.transformNumber(development?.information?.priceTo);

    const bedroomMax = bedrooms.length > 1 ? bedrooms[1] : bedrooms[0];
    const bathMax = bathrooms.length > 1 ? bathrooms[1] : bathrooms[0];
    const carspaceMax = carSpaces.length > 1 ? carSpaces[1] : carSpaces[0];
    const studyMax = study.length > 1 ? study[1] : study[0];

    const project: BrazeProject = {
      project_name: development?.title,
      page_url: this.window?.location?.href,
      project_type: development.projectTypes,
      project_size: development.totalProperties,
      project_state: development?.address?.state,
      project_region: development?.address?.region,
      project_postcode: development.address.postalCode,
      project_from_price: priceFrom,
      project_to_price: priceTo,
      project_min_bed: bedrooms[0],
      project_max_bed: bedroomMax,
      project_min_bath: bathrooms[0],
      project_max_bath: bathMax,
      project_min_carpark: carSpaces[0],
      project_max_carpark: carspaceMax,
      project_min_study: study[0],
      project_max_study: studyMax,
      project_expected_completion: development?.details?.completionDate,
    };

    return project;
  }

  getBrazeChildList(property: Properties): BrazeChildList {
    const childList: BrazeChildList = {
      child_listing_name: property?.title,
      child_listing_price: this.transformNumber(property?.price),
      child_listing_bed: this.transformNumber(property?.bedrooms),
      child_listing_bath: this.transformNumber(property?.bathrooms),
      child_listing_car: this.transformNumber(property?.carSpaces),
      child_listing_study: this.transformNumber(property?.studyroom),
      child_listing_sqm: property?.areaTotal?.toString(),
    };

    return childList;
  }

  getPageDetails(): PageDetails {
    let postCode = null;
    let suburb = null;
    const pathName = this.window?.location?.pathname?.split('/');
    let pathNameArr = [...new Set(pathName)];
    pathNameArr = pathName.filter((x): x is string => x !== null && x != '');

    const state = pathNameArr[1];
    if (pathNameArr[2]) {
      const suburbPostcode = pathNameArr[2];
      const suburbPostCodeArr = suburbPostcode.split('-');
      postCode = suburbPostCodeArr[suburbPostCodeArr.length - 1];
      suburbPostCodeArr.splice(-1, 1);
      suburb = this.capitalizeWords(suburbPostCodeArr.join(' '));
    }

    const searchPage: PageDetails = {
      page_url: this.window?.location?.href,
      page_state: state,
      page_suburb: suburb,
      page_postcode: postCode,
    };

    return searchPage;
  }

  getRange(data: string): Array<number> {
    const result = [];

    if (data) {
      const range = data.split('-');

      range.forEach(item => {
        result.push(Number(item));
      });

      return result.sort();
    }

    return [0];
  }

  capitalizeWords(str: string): string {
    return str
      .split(' ')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  transformNumber(data: string): number {
    if (data) {
      const result = parseFloat(data.replace(/,/g, ''));

      return result;
    }

    return 0;
  }
}
